import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

function ProtectedAdminRoute({ children }) {
  const [status, setStatus] = useState("checking");
  const [userEmail, setUserEmail] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [closingSession, setClosingSession] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserEmail("");
        setStatus("not-authenticated");
        return;
      }

      setUserEmail(user.email || "Sesión activa");

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          setStatus("not-admin");
          return;
        }

        const userData = userSnap.data();

        if (userData.role === "admin") {
          setStatus("admin");
        } else {
          setStatus("not-admin");
        }
      } catch (error) {
        console.error("Error verificando permisos:", error);
        setStatus("not-admin");
      }
    });

    return () => unsubscribe();
  }, []);

  async function handleLogout() {
    try {
      setClosingSession(true);

      await signOut(auth);

      setShowLogoutModal(false);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    } finally {
      setClosingSession(false);
    }
  }

  if (status === "checking") {
    return (
      <main className="loading-page">
        <section className="loading-card">
          <div className="banter-loader" aria-hidden="true">
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
            <div className="banter-loader__box"></div>
          </div>

          <h2>Verificando permisos...</h2>
          <p>Espera un momento.</p>
        </section>
      </main>
    );
  }

  if (status === "not-authenticated") {
    return <Navigate to="/login" replace />;
  }

  if (status === "not-admin") {
    return (
      <>
        <main className="loading-page">
          <section className="loading-card loading-card--denied">
            <div className="loading-denied-icon">🚫</div>

            <h1>Acceso denegado</h1>

            <p>
              Tu usuario no tiene permisos de administrador para ingresar al
              panel.
            </p>

            <div className="denied-user-info">
              <span>Sesión iniciada como</span>
              <strong>{userEmail}</strong>
            </div>

            <button
              className="primary-action-button denied-logout-button"
              type="button"
              onClick={() => setShowLogoutModal(true)}
              disabled={closingSession}
            >
              Cerrar sesión
            </button>
          </section>
        </main>

        {showLogoutModal && (
          <div
            className="logout-modal-backdrop"
            onClick={() => {
              if (!closingSession) {
                setShowLogoutModal(false);
              }
            }}
          >
            <section
              className="logout-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="logout-modal__icon">👤</div>

              <h2>Cerrar sesión</h2>

              <p>
                Estás conectado como <strong>{userEmail}</strong>. ¿Quieres
                cerrar tu sesión actual?
              </p>

              <div className="logout-modal__actions">
                <button
                  className="small-button"
                  type="button"
                  onClick={() => setShowLogoutModal(false)}
                  disabled={closingSession}
                >
                  Cancelar
                </button>

                <button
                  className="small-button small-button--danger"
                  type="button"
                  onClick={handleLogout}
                  disabled={closingSession}
                >
                  {closingSession ? "Cerrando..." : "Cerrar sesión"}
                </button>
              </div>
            </section>
          </div>
        )}
      </>
    );
  }

  return <div className="app-shell">{children}</div>;
}

export default ProtectedAdminRoute;